/**
 * FASE 1B - Pagina Uscite
 * Gestione completa spese con categorie, IVA, ricorrenza, centri di costo
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowDownCircle, Euro, Search, Filter, Plus, Calendar, 
  ChevronLeft, ChevronRight, Building, FileText, X, Check, 
  Edit2, Trash2, RefreshCw, Paperclip, Repeat, Tag
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

const EXPENSE_CATEGORIES = [
  { value: 'fitto', label: 'Fitto/Affitto', icon: '🏠', color: 'indigo' },
  { value: 'pulizie', label: 'Pulizie', icon: '🧹', color: 'blue' },
  { value: 'lavanderia', label: 'Lavanderia', icon: '👕', color: 'cyan' },
  { value: 'utenze', label: 'Utenze', icon: '⚡', color: 'yellow', subcategories: ['luce', 'gas', 'acqua', 'internet', 'telefono'] },
  { value: 'commissioni_ota', label: 'Commissioni OTA', icon: '💳', color: 'orange' },
  { value: 'manutenzione', label: 'Manutenzione', icon: '🔧', color: 'gray', subcategories: ['idraulico', 'elettricista', 'giardinaggio', 'riparazioni'] },
  { value: 'marketing', label: 'Marketing', icon: '📣', color: 'pink', subcategories: ['pubblicita_online', 'foto_video', 'stampa'] },
  { value: 'software', label: 'Software', icon: '💻', color: 'purple', subcategories: ['pms', 'channel_manager', 'contabilita'] },
  { value: 'assicurazioni', label: 'Assicurazioni', icon: '🛡️', color: 'green' },
  { value: 'arredi', label: 'Arredi', icon: '🪑', color: 'amber' },
  { value: 'tasse', label: 'Tasse', icon: '📄', color: 'red', subcategories: ['imu', 'tari', 'cedolare_secca', 'imposta_soggiorno'] },
  { value: 'altro', label: 'Altro', icon: '📁', color: 'slate' }
];

const SUBCATEGORY_LABELS = {
  luce: 'Luce',
  gas: 'Gas',
  acqua: 'Acqua',
  internet: 'Internet',
  telefono: 'Telefono',
  idraulico: 'Idraulico',
  elettricista: 'Elettricista',
  giardinaggio: 'Giardinaggio',
  riparazioni: 'Riparazioni',
  pubblicita_online: 'Pubblicità Online',
  foto_video: 'Foto/Video',
  stampa: 'Stampa',
  pms: 'PMS',
  channel_manager: 'Channel Manager',
  contabilita: 'Contabilità',
  imu: 'IMU',
  tari: 'TARI',
  cedolare_secca: 'Cedolare Secca',
  imposta_soggiorno: 'Imposta di Soggiorno'
};

const PAYMENT_METHODS = [
  { value: 'contanti', label: 'Contanti' },
  { value: 'carta', label: 'Carta' },
  { value: 'bonifico', label: 'Bonifico' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'rid', label: 'RID/Addebito' },
  { value: 'altro', label: 'Altro' }
];

const STATI = [
  { value: 'da_pagare', label: 'Da Pagare', color: 'amber' },
  { value: 'parziale', label: 'Parziale', color: 'orange' },
  { value: 'pagata', label: 'Pagata', color: 'green' }
];

const RECURRENCE_TYPES = [
  { value: 'nessuna', label: 'Nessuna' },
  { value: 'mensile', label: 'Mensile' },
  { value: 'bimestrale', label: 'Bimestrale' },
  { value: 'trimestrale', label: 'Trimestrale' },
  { value: 'semestrale', label: 'Semestrale' },
  { value: 'annuale', label: 'Annuale' }
];

export default function EconomicExpenses() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [totals, setTotals] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [units, setUnits] = useState([]);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterFornitore, setFilterFornitore] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterCentroCosto, setFilterCentroCosto] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        anno: selectedYear,
        mese: selectedMonth,
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (filterCategoria) params.append('categoria', filterCategoria);
      if (filterFornitore) params.append('fornitore', filterFornitore);
      if (filterStato) params.append('stato', filterStato);
      if (filterCentroCosto) params.append('centro_costo', filterCentroCosto);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${API}/admin/economic/expenses?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setExpenses(response.data.expenses || []);
      setTotals(response.data.totals || {});
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Errore nel caricamento delle spese');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth, pagination.page, pagination.limit, filterCategoria, filterFornitore, filterStato, filterCentroCosto, searchQuery]);

  const fetchUnits = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/admin/economic/units`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchExpenses();
      fetchUnits();
    }
  }, [token, fetchExpenses, fetchUnits]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (dateStr.length === 7) return dateStr; // YYYY-MM format
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const openCreateModal = () => {
    setEditingExpense(null);
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      categoria: 'altro',
      sottocategoria: '',
      fornitore: '',
      descrizione: '',
      importo: '',
      iva: 0,
      data_documento: today,
      data_competenza: today,
      stato: 'da_pagare',
      ricorrente: false,
      ricorrenza_tipo: 'nessuna'
    });
    setShowModal(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      categoria: expense.categoria,
      sottocategoria: expense.sottocategoria,
      fornitore: expense.fornitore,
      descrizione: expense.descrizione,
      importo: expense.importo,
      iva: expense.iva || 0,
      data_documento: expense.data_documento,
      data_competenza: expense.data_competenza,
      data_pagamento: expense.data_pagamento,
      metodo_pagamento: expense.metodo_pagamento,
      stato: expense.stato,
      ricorrente: expense.ricorrente || false,
      ricorrenza_tipo: expense.ricorrenza_tipo || 'nessuna',
      ricorrenza_fine: expense.ricorrenza_fine,
      centro_costo: expense.centro_costo,
      note: expense.note
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.descrizione || !formData.importo) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        importo: parseFloat(formData.importo) || 0,
        iva: parseFloat(formData.iva) || 0,
        ricorrente: formData.ricorrente === true || formData.ricorrente === 'true'
      };
      
      if (editingExpense) {
        await axios.put(
          `${API}/admin/economic/expenses/${editingExpense.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Spesa aggiornata');
      } else {
        await axios.post(
          `${API}/admin/economic/expenses`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Spesa creata');
      }
      setShowModal(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa spesa?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/economic/expenses/${expense.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Spesa eliminata');
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getCategoryInfo = (cat) => {
    return EXPENSE_CATEGORIES.find(c => c.value === cat) || { label: cat, icon: '📁', color: 'gray' };
  };

  const getSelectedCategorySubcats = () => {
    const cat = EXPENSE_CATEGORIES.find(c => c.value === formData.categoria);
    return cat?.subcategories || [];
  };

  return (
    <AdminLayout title="Gestione Uscite">
      <div className="space-y-4 pb-20 md:pb-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-xl md:text-2xl text-[#1A202C]">Uscite</h1>
            <p className="text-sm text-gray-500">Gestione spese e costi</p>
          </div>
          
          {/* Month Navigator */}
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1 w-full md:w-auto justify-center">
            <Button variant="ghost" size="sm" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border-0 bg-transparent font-medium text-center appearance-none cursor-pointer text-sm"
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border-0 bg-transparent text-gray-500 appearance-none cursor-pointer text-sm"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Imponibile</p>
              <p className="text-lg md:text-xl font-bold text-gray-600">{formatCurrency(totals.importo)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">IVA</p>
              <p className="text-lg md:text-xl font-bold text-blue-600">{formatCurrency(totals.iva)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Totale</p>
              <p className="text-lg md:text-xl font-bold text-red-600">{formatCurrency(totals.totale)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Da Pagare</p>
              <p className="text-lg md:text-xl font-bold text-amber-600">{formatCurrency(totals.da_pagare)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Cerca fornitore, descrizione..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={filterCategoria}
              onChange={(e) => setFilterCategoria(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Tutte le categorie</option>
              {EXPENSE_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
              ))}
            </select>
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Tutti gli stati</option>
              {STATI.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="hidden md:flex"
            >
              <Filter className="w-4 h-4 mr-1" />
              Filtri
            </Button>
            <Button variant="outline" size="sm" onClick={fetchExpenses}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <Card className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Fornitore</label>
                <Input
                  placeholder="Nome fornitore..."
                  value={filterFornitore}
                  onChange={(e) => setFilterFornitore(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Centro di Costo</label>
                <select
                  value={filterCentroCosto}
                  onChange={(e) => setFilterCentroCosto(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Tutti</option>
                  <option value="generale">Generale</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end col-span-2 md:col-span-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterCategoria('');
                    setFilterFornitore('');
                    setFilterStato('');
                    setFilterCentroCosto('');
                    setSearchQuery('');
                  }}
                >
                  Reset filtri
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Data Table / Cards */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A059]"></div>
          </div>
        ) : expenses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ArrowDownCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Nessuna spesa trovata</p>
              <Button onClick={openCreateModal} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi Spesa
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Data</th>
                          <th className="text-left p-3 font-medium text-gray-600">Categoria</th>
                          <th className="text-left p-3 font-medium text-gray-600">Fornitore</th>
                          <th className="text-left p-3 font-medium text-gray-600">Descrizione</th>
                          <th className="text-right p-3 font-medium text-gray-600">Importo</th>
                          <th className="text-right p-3 font-medium text-gray-600">IVA</th>
                          <th className="text-right p-3 font-medium text-gray-600">Totale</th>
                          <th className="text-center p-3 font-medium text-gray-600">Stato</th>
                          <th className="text-center p-3 font-medium text-gray-600"></th>
                          <th className="text-right p-3 font-medium text-gray-600">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.map((expense) => {
                          const catInfo = getCategoryInfo(expense.categoria);
                          return (
                            <tr key={expense.id} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-gray-600">{formatDate(expense.data_documento || expense.data_competenza)}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs bg-${catInfo.color}-100 text-${catInfo.color}-700`}>
                                  {catInfo.icon} {catInfo.label}
                                </span>
                                {expense.sottocategoria && (
                                  <div className="text-xs text-gray-500 mt-1">{SUBCATEGORY_LABELS[expense.sottocategoria] || expense.sottocategoria}</div>
                                )}
                              </td>
                              <td className="p-3 text-gray-800">{expense.fornitore || '-'}</td>
                              <td className="p-3 max-w-[200px] truncate" title={expense.descrizione}>{expense.descrizione}</td>
                              <td className="p-3 text-right">{formatCurrency(expense.importo)}</td>
                              <td className="p-3 text-right text-gray-500">{formatCurrency(expense.iva)}</td>
                              <td className="p-3 text-right font-bold text-red-600">{formatCurrency(expense.totale)}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  expense.stato === 'pagata' ? 'bg-green-100 text-green-700' : 
                                  expense.stato === 'parziale' ? 'bg-orange-100 text-orange-700' : 
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {STATI.find(s => s.value === expense.stato)?.label || expense.stato}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                {expense.ricorrente && (
                                  <span title="Spesa ricorrente" className="text-purple-500">
                                    <Repeat className="w-4 h-4" />
                                  </span>
                                )}
                                {expense.allegato_url && (
                                  <a href={expense.allegato_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 ml-1">
                                    <Paperclip className="w-4 h-4 inline" />
                                  </a>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button variant="ghost" size="sm" onClick={() => openEditModal(expense)}>
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(expense)} className="text-red-500">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {expenses.map((expense) => {
                const catInfo = getCategoryInfo(expense.categoria);
                return (
                  <Card key={expense.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={`px-2 py-1 rounded-full text-xs bg-${catInfo.color}-100 text-${catInfo.color}-700`}>
                            {catInfo.icon} {catInfo.label}
                          </span>
                          {expense.ricorrente && (
                            <span className="ml-2 text-purple-500">
                              <Repeat className="w-3 h-3 inline" />
                            </span>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          expense.stato === 'pagata' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {STATI.find(s => s.value === expense.stato)?.label || expense.stato}
                        </span>
                      </div>
                      
                      <div className="font-medium text-gray-800 mb-1">{expense.descrizione}</div>
                      
                      <div className="text-sm text-gray-500 mb-3">
                        {expense.fornitore && <span>{expense.fornitore} • </span>}
                        <span>{formatDate(expense.data_documento || expense.data_competenza)}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-center mb-3">
                        <div className="bg-gray-50 rounded p-2">
                          <div className="text-xs text-gray-500">Importo</div>
                          <div className="font-medium">{formatCurrency(expense.importo)}</div>
                        </div>
                        <div className="bg-gray-50 rounded p-2">
                          <div className="text-xs text-gray-500">IVA</div>
                          <div className="font-medium">{formatCurrency(expense.iva)}</div>
                        </div>
                        <div className="bg-red-50 rounded p-2">
                          <div className="text-xs text-gray-500">Totale</div>
                          <div className="font-bold text-red-600">{formatCurrency(expense.totale)}</div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(expense)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(expense)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-4 py-2 text-sm">
              Pagina {pagination.page} di {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Mobile FAB */}
        <div className="md:hidden fixed bottom-20 right-4 z-50">
          <Button
            onClick={openCreateModal}
            className="rounded-full w-14 h-14 shadow-lg bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>

        {/* Desktop Add Button */}
        <div className="hidden md:flex justify-end">
          <Button onClick={openCreateModal} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Spesa
          </Button>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingExpense ? 'Modifica Spesa' : 'Nuova Spesa'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Categoria e Sottocategoria */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Categoria *</label>
                    <select
                      value={formData.categoria || 'altro'}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value, sottocategoria: '' })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {EXPENSE_CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Sottocategoria</label>
                    <select
                      value={formData.sottocategoria || ''}
                      onChange={(e) => setFormData({ ...formData, sottocategoria: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                      disabled={getSelectedCategorySubcats().length === 0}
                    >
                      <option value="">Nessuna</option>
                      {getSelectedCategorySubcats().map(sub => (
                        <option key={sub} value={sub}>{SUBCATEGORY_LABELS[sub] || sub}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Fornitore e Descrizione */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Fornitore</label>
                    <Input
                      value={formData.fornitore || ''}
                      onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })}
                      placeholder="Nome fornitore..."
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Descrizione *</label>
                    <Input
                      value={formData.descrizione || ''}
                      onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                      placeholder="Descrizione spesa..."
                    />
                  </div>
                </div>

                {/* Importi */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Importo *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.importo || ''}
                      onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">IVA</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.iva || ''}
                      onChange={(e) => setFormData({ ...formData, iva: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Totale</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={(parseFloat(formData.importo || 0) + parseFloat(formData.iva || 0)).toFixed(2)}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                </div>

                {/* Date */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Documento *</label>
                    <Input
                      type="date"
                      value={formData.data_documento || ''}
                      onChange={(e) => setFormData({ ...formData, data_documento: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Competenza *</label>
                    <Input
                      type="date"
                      value={formData.data_competenza || ''}
                      onChange={(e) => setFormData({ ...formData, data_competenza: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Pagamento</label>
                    <Input
                      type="date"
                      value={formData.data_pagamento || ''}
                      onChange={(e) => setFormData({ ...formData, data_pagamento: e.target.value })}
                    />
                  </div>
                </div>

                {/* Pagamento e Stato */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Metodo Pagamento</label>
                    <select
                      value={formData.metodo_pagamento || ''}
                      onChange={(e) => setFormData({ ...formData, metodo_pagamento: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="">Seleziona...</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Stato *</label>
                    <select
                      value={formData.stato || 'da_pagare'}
                      onChange={(e) => setFormData({ ...formData, stato: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {STATI.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ricorrenza */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ricorrente"
                      checked={formData.ricorrente || false}
                      onChange={(e) => setFormData({ ...formData, ricorrente: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="ricorrente" className="text-sm text-gray-600">Spesa ricorrente</label>
                  </div>
                  {formData.ricorrente && (
                    <>
                      <div>
                        <label className="text-sm text-gray-600 block mb-1">Frequenza</label>
                        <select
                          value={formData.ricorrenza_tipo || 'mensile'}
                          onChange={(e) => setFormData({ ...formData, ricorrenza_tipo: e.target.value })}
                          className="w-full border rounded-md px-3 py-2"
                        >
                          {RECURRENCE_TYPES.filter(r => r.value !== 'nessuna').map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 block mb-1">Fine Ricorrenza</label>
                        <Input
                          type="date"
                          value={formData.ricorrenza_fine || ''}
                          onChange={(e) => setFormData({ ...formData, ricorrenza_fine: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Centro di Costo */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Centro di Costo</label>
                  <select
                    value={formData.centro_costo || ''}
                    onChange={(e) => setFormData({ ...formData, centro_costo: e.target.value })}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">Generale</option>
                    {units.map(u => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </div>

                {/* Note */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Note</label>
                  <textarea
                    value={formData.note || ''}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 h-20"
                    placeholder="Note aggiuntive..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowModal(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Salva
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
